/*
Copyright Captcha, Inc. 2004-2021. All rights reserved.

BotDetect, BotDetect CAPTCHA, Lanap, Lanap CAPTCHA, Lanap BotDetect, 
Lanap BotDetect CAPTCHA, Lanapsoft, Lanapsoft CAPTCHA, 
Lanapsoft BotDetect, Lanapsoft BotDetect CAPTCHA, and Lanap Software 
are trademarks of Captcha, Inc. All other product, brand, and company 
names are mentioned for identification purposes only and are trademarks 
or registered trademarks of their respective owners.

Captcha, Inc. - formerly: Lanapsoft, Inc. / Lanap, Inc.
*/

// BotDetect CAPTCHA JavaScript Module

import httpGet from './helpers/http-client';
import currentTimestamp from './helpers/time';
import createUrl from './helpers/create-url';
import createScriptElement from './helpers/create-script-element';
import { mergeSettings } from './helpers/object';
import changeRelativeToAbsoluteUrls from './helpers/captcha-endpoint-path';
import CaptchaStyleNameNotSetException from './exceptions/captcha-stylename-not-set-exception';
import CaptchaEndpointPathNotSetException from './exceptions/captcha-endpoint-path-not-set-exception';
import CaptchaPlaceholderNotFoundException from './exceptions/captcha-placeholder-not-found-exception';

// default captcha settings
const defaultSettings = {
  // property to point to the captcha endpoint path on your app's backend
  captchaEndpointPath: ''
};

const defaultIntegrationType = '0'; // unknown integration type

class JsCaptcha {

  constructor(settings) {
    this._settings = mergeSettings(defaultSettings, settings);
  }

  static getInstance(settings) {
    return new JsCaptcha(settings);
  }

  get settings() {
    return this._settings;
  }

  get captchaEndpointPath() {
    return this.settings.captchaEndpointPath;
  }

  get captchaStyleName() {
    return this._captchaStyleName;
  }

  set captchaStyleName(name) {
    this._captchaStyleName = name;
  }

  set integrationType(type) {
    this._integrationType = type;
  }

  get integrationType() {
    return (this._integrationType === void 0) ?
              defaultIntegrationType
              : this._integrationType;
  }

  getCaptchaData() {
    return this._getBDClientSideInstance().getCaptchaData();
  }

  reloadImage() {
    this._getBDClientSideInstance().reloadImage();
  }

  getHtml(captchaStyleName, callback) {
    if (!this.captchaEndpointPath) {
      throw new CaptchaEndpointPathNotSetException();
    }

    this.captchaStyleName = captchaStyleName;
    
    const self = this;
    const url = createUrl(this.captchaEndpointPath, {
      get: 'html',
      c: captchaStyleName,
      ji: false,
      _: currentTimestamp()
    });
    httpGet(url, {}, function(captchaHtml) {
      captchaHtml = changeRelativeToAbsoluteUrls(captchaHtml, self.captchaEndpointPath);
      callback(captchaHtml);
      self._loadScriptIncludes();
    });
  }

  displayCaptcha(placeholderId) {
    const placeholder = document.getElementById(placeholderId);
    
    if (!placeholder) {
      throw new CaptchaPlaceholderNotFoundException(placeholderId);
    }

    const captchaStyleName = placeholder.getAttribute('data-captchastylename');

    if (!captchaStyleName) {
      throw new CaptchaStyleNameNotSetException();
    }

    this.getHtml(captchaStyleName, function(captchaHtml) {
      placeholder.innerHTML = captchaHtml;
    });
  }

  _getBDClientSideInstance() {
    if (window.botdetect === void 0) {
      return null;
    }

    if (!this.captchaStyleName) {
      throw new CaptchaStyleNameNotSetException();
    }

    return window.botdetect.getInstanceByStyleName(this.captchaStyleName);
  }

  _loadScriptIncludes() {
    // remove included script if it exists
    const includedScript = document.getElementById('bdc_script_include');
    if (includedScript) {
      includedScript.parentNode.removeChild(includedScript);
    }

    // include new script to boby
    const captchaId = document.getElementById('BDC_VCID_' + this.captchaStyleName).value;
    const scriptIncludeUrl = createUrl(this.captchaEndpointPath, {
      get: 'script-include',
      c: this.captchaStyleName,
      t: captchaId,
      cs: this.integrationType
    });
    const scriptIncludeElement = createScriptElement(scriptIncludeUrl);
    document.body.appendChild(scriptIncludeElement);
  }
}

function getJsCaptchaInstance(settings) {
  return JsCaptcha.getInstance(settings);
}

(function(window) {
  window.JsCaptcha = JsCaptcha;
  window.getJsCaptchaInstance = getJsCaptchaInstance;
})(window);

export { JsCaptcha, getJsCaptchaInstance };
